import {
  Fog,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  WebGLRenderer,
} from 'three';

// Color that matches the CSS body gradient's horizon stop. Used as initial
// fog color so the ground plane fades cleanly before SunRig boots and
// starts driving the fog colour from its palette each frame.
const HORIZON_COLOR = 0xc9a878;

// Sun + ambient lights are NOT created here anymore — they live on SunRig
// (src/atmosphere/sunRig.ts), which owns the entire palette and drives them
// from the TimeOfDayClock each frame. createScene only sets up the things
// SunRig doesn't touch: the fog, the ground plane, the scene container.
export function createScene(): Scene {
  const scene = new Scene();
  // CloudSky paints a sky dome that fills every background pixel from frame
  // one, so the renderer is alpha:false (opaque canvas — PF8): nothing ever
  // shows through it, and the compositor no longer page-blends the canvas
  // every frame. The #lec-loading overlay covers the pre-first-render instant,
  // and the clear colour is sky-toned as a final fallback.
  scene.background = null;
  scene.fog = new Fog(HORIZON_COLOR, 35, 110);

  // Ground plane extending to the fog horizon. y matches the photoscan's dirt
  // level so the boundary blends rather than floating/clipping.
  const floor = new Mesh(
    new PlaneGeometry(500, 500),
    new MeshStandardMaterial({ color: 0xa68a64, roughness: 0.95 }),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -0.6;
  scene.add(floor);

  return scene;
}

export function createCamera(width: number, height: number): PerspectiveCamera {
  // 75° vertical FOV ≈ 24mm equivalent on full-frame — wide enough to feel
  // immersive in the interior without distorting facades from outside.
  const camera = new PerspectiveCamera(75, width / height, 0.1, 200);
  camera.position.set(12, 5, 8);
  camera.lookAt(2.0, 1.5, -1.6);
  return camera;
}

export function createRenderer(): WebGLRenderer {
  // alpha:false (PF8) — the CloudSky dome + room cover every pixel each frame,
  // so the canvas never needs to composite over the page. Opaque canvas drops
  // the all-session per-frame page-blend. Clear colour is the horizon tone so
  // the (overlay-covered) pre-first-render instant is sky, never black.
  const renderer = new WebGLRenderer({ antialias: true, alpha: false });
  renderer.setClearColor(HORIZON_COLOR, 1);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  return renderer;
}
